import type { ResponseGetCategories_categoryId_Racks } from "@CommonApi/categories/_categoryId_/racks";
import { VariantRestrictionCondition } from "@CommonApi/merchflow/parse-variant-restrictions";
import { SelectOption } from "src/elements/Select/store/types";
import type { TranslationKey } from "src/i18n/hooks/UseTranslation";
import { translate } from "src/i18n/translate";
import { ALL_BAYS_SHELVES } from "./types";

/**
 * The backend's shelf-rule tokens that reach racks (see `rackNameOf`): "Rack" (every rack
 * board), "Not rack" (every wall row), "Rack: <name>" (one rack's boards) and
 * "Not rack: <name>" (every board but that rack's, other racks included). Positional rules
 * (Top, Bottom - 1, 3) never land on a rack tier.
 */
const RACK_TOKEN = "Rack";
const NOT_RACK_TOKEN = "Not rack";

/** A rack shelf rule. `name` null is the plain token: any rack / no rack. */
export interface RackShelfTarget {
  kind: "rack" | "notRack";
  name: string | null;
}

const normalizeRackName = (name: string) => name.trim().toUpperCase().replace(/\s+/g, " ");

/** The category's rack names, once each, sorted; [] while the request loads or when it fails. */
export const rackNamesOf = (response: ResponseGetCategories_categoryId_Racks | undefined) =>
  [...new Set((response?.racks ?? []).map((rack) => normalizeRackName(rack.name)))].sort();

type DescribeKey = TranslationKey<"planogramDelivery">;

const t = (key: DescribeKey, params?: Record<string, string>) =>
  translate("planogramDelivery", key, params);

const RACK_RULE_KEYS = {
  MUST_HAVE: "variantRestrictionDescribe.rackRuleMustHave",
  CAN_HAVE: "variantRestrictionDescribe.rackRuleMayHave",
  MUST_HAVE_ONLY: "variantRestrictionDescribe.rackRuleOnlyThese",
  MUST_NOT_HAVE: "variantRestrictionDescribe.rackRuleMustNotHave",
} as const satisfies Record<VariantRestrictionCondition, DescribeKey>;

const RACK_RULE = /^(not\s+)?rack(?:\s*:\s*(.*))?$/i;

/** The rack target a shelf rule names, or null for a positional / wildcard rule. */
export const parseRackShelfRule = (shelfRule: string): RackShelfTarget | null => {
  const match = RACK_RULE.exec(shelfRule.trim());
  if (!match) return null;
  // "Rack:" with no name is the plain token, as the backend reads it.
  const name = normalizeRackName(match[2] ?? "") || null;
  return { kind: match[1] ? "notRack" : "rack", name };
};

const formatRackShelfRule = ({ kind, name }: RackShelfTarget): string => {
  const token = kind === "rack" ? RACK_TOKEN : NOT_RACK_TOKEN;
  return name ? `${token}: ${name}` : token;
};

const POSITIONAL_SHELF_RULES = [ALL_BAYS_SHELVES, "Top", "Bottom"] as const;

const POSITIONAL_LABELS: Record<(typeof POSITIONAL_SHELF_RULES)[number], DescribeKey> = {
  [ALL_BAYS_SHELVES]: "variantRestrictionDescribe.shelfOptionAll",
  Top: "variantRestrictionDescribe.shelfOptionTop",
  Bottom: "variantRestrictionDescribe.shelfOptionBottom",
};

/** Each rack's "Rack: <name>" / "Not rack: <name>", the plain tokens, then the positions. */
export const shelfRuleOptions = (rackNames: readonly string[]): SelectOption<string>[] => {
  const named = rackNames.flatMap((name) => [
    { kind: "rack" as const, name },
    { kind: "notRack" as const, name },
  ]);
  return [
    ...named.map((target) => ({
      value: formatRackShelfRule(target),
      label: t(
        target.kind === "rack"
          ? "variantRestrictionDescribe.shelfOptionRackNamed"
          : "variantRestrictionDescribe.shelfOptionNotRackNamed",
        { name: target.name },
      ),
    })),
    { value: RACK_TOKEN, label: t("variantRestrictionDescribe.shelfOptionRack") },
    { value: NOT_RACK_TOKEN, label: t("variantRestrictionDescribe.shelfOptionNotRack") },
    ...POSITIONAL_SHELF_RULES.map((value) => ({
      value,
      label: t(POSITIONAL_LABELS[value]),
    })),
  ];
};

/**
 * A rack rule in words, e.g. "Flushable wipes: only on WIPES RACK" for MUST_NOT_HAVE on
 * "Not rack: WIPES RACK". Null when the rule does not target a rack.
 */
export const rackRuleText = (rule: {
  variant: string;
  condition: VariantRestrictionCondition;
  bayNumberRule: string;
  shelfRule: string;
}): string | null => {
  const target = parseRackShelfRule(rule.shelfRule);
  if (!target) return null;

  const rackWords = (name: string | null) =>
    name ?? t("variantRestrictionDescribe.rackRuleAnyRack");

  const variant = rule.variant.trim() || "—";
  let text: string;
  if (target.kind === "notRack" && rule.condition === "MUST_NOT_HAVE") {
    text = t("variantRestrictionDescribe.rackRuleOnlyOn", {
      variant,
      target: rackWords(target.name),
    });
  } else {
    const where =
      target.kind === "rack"
        ? rackWords(target.name)
        : target.name
          ? t("variantRestrictionDescribe.rackRuleEverywhereBut", { name: target.name })
          : t("variantRestrictionDescribe.rackRuleWallShelves");
    text = t(RACK_RULE_KEYS[rule.condition], { variant, target: where });
  }

  const bay = rule.bayNumberRule.trim();
  return bay && bay !== ALL_BAYS_SHELVES
    ? `${text} · ${t("variantRestrictionDescribe.bay")} ${bay}`
    : text;
};
